import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, BellOff, Pin, PinOff } from "lucide-react";
import {
  ConversaResumo,
  marcarLido,
  marcarNaoLido,
  fixarConversa,
  desfixarConversa,
} from "../services/chat";
import { useEscape } from "../hooks/useEscape";

interface Props {
  conversa: ConversaResumo;
  userId?: string;
  // Coordenadas em viewport (event.clientX/Y)
  x: number;
  y: number;
  onClose: () => void;
}

// Menu de contexto que aparece no clique-direito de um item da lista.
// Posicionamento clamped pra não vazar da viewport.
export function ChatContextMenu({ conversa, userId, x, y, onClose }: Props) {
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  useEscape(true, onClose);

  // Após render, mede o tamanho do menu e clampa pra dentro da janela
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margem = 8;
    let left = x;
    let top = y;
    if (left + rect.width + margem > window.innerWidth) left = window.innerWidth - rect.width - margem;
    if (top + rect.height + margem > window.innerHeight) top = window.innerHeight - rect.height - margem;
    if (left < margem) left = margem;
    if (top < margem) top = margem;
    setPos({ left, top });
  }, [x, y]);

  const meuPart = conversa.participantes.find((p) => p.usuarioId === userId);
  const ehNaoLida = (conversa.naoLidas || 0) > 0 || !!conversa.marcadoNaoLido;
  const fixada = !!meuPart?.fixadoEm;
  const ehIa = conversa.tipo === "ia";

  const lidoM = useMutation({
    mutationFn: () => marcarLido(conversa.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-conversas"] }),
  });
  const naoLidoM = useMutation({
    mutationFn: () => marcarNaoLido(conversa.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-conversas"] }),
  });
  const fixarM = useMutation({
    mutationFn: () => (fixada ? desfixarConversa(conversa.id) : fixarConversa(conversa.id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-conversas"] }),
  });

  function rodar(fn: () => void) {
    fn();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[70]"
      onClick={onClose}
      onContextMenu={(e) => { e.preventDefault(); onClose(); }}
    >
      <div
        ref={ref}
        role="menu"
        className="absolute bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1 min-w-[200px] text-sm"
        style={{ left: pos.left, top: pos.top }}
        onClick={(e) => e.stopPropagation()}
      >
        {ehNaoLida ? (
          <MenuItem
            icon={<Check size={14} />}
            onClick={() => rodar(() => lidoM.mutate())}
            disabled={lidoM.isPending}
          >
            Marcar como lido
          </MenuItem>
        ) : (
          <MenuItem
            icon={<BellOff size={14} />}
            onClick={() => rodar(() => naoLidoM.mutate())}
            disabled={naoLidoM.isPending}
          >
            Marcar como não lido
          </MenuItem>
        )}

        {!ehIa && (
          <>
            <Separador />
            <MenuItem
              icon={fixada ? <PinOff size={14} /> : <Pin size={14} />}
              onClick={() => rodar(() => fixarM.mutate())}
              disabled={fixarM.isPending}
            >
              {fixada ? "Desafixar conversa" : "Fixar no topo"}
            </MenuItem>
          </>
        )}
      </div>
    </div>
  );
}

function MenuItem({ icon, children, onClick, disabled }: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-2 px-3 py-2 text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
    >
      <span className="text-slate-500 dark:text-slate-400 shrink-0">{icon}</span>
      <span>{children}</span>
    </button>
  );
}

function Separador() {
  return <div className="my-1 border-t border-slate-100 dark:border-slate-700" />;
}
